import type { TenantConfig } from '@/types/tenant.types';

/**
 * App language resolution — mirrors Flutter LocalDataService.getAppLanguage():
 * the stored language, else the browser language when the tenant supports it, else 'en'.
 *
 * Kept free of other app imports so apiClient, the i18n setup and the stores can all use it.
 */

const APP_LANGUAGE_KEY = 'appLanguage';
const TENANT_CONFIG_KEY = 'tenantConfig';

function readStorage(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

/**
 * Mirrors Flutter GenericHelpers.getBrowserLanguageCode(): navigator.language's primary
 * subtag, if the cached tenant config (language: 'config') lists it; otherwise 'en'.
 */
export function getBrowserLanguageCode(): string {
  const browserLanguageCode = (navigator.language ?? '').split('-')[0];
  let config: TenantConfig | null = null;
  try {
    config = JSON.parse(readStorage(TENANT_CONFIG_KEY) ?? 'null') as TenantConfig | null;
  } catch {
    config = null;
  }
  const supported = config?.supportedLanguage ?? [];
  return supported.some((l) => l.code === browserLanguageCode) ? browserLanguageCode : 'en';
}

/** Mirrors Flutter LocalDataService.getAppLanguage(). */
export function getAppLanguage(): string {
  return readStorage(APP_LANGUAGE_KEY) || getBrowserLanguageCode();
}
